/**
 * 顶部菜单可见性（§4.5）
 *
 * 与 Redux `state.app.permissionCodes.menuCodes`、`permissionCodesLoaded` 配合使用。
 * - 侧栏数据来源：`AppHeader/menus.js` 的 `items` 配置。
 * - 路由守卫：`ClientOutletGuard` + `clientPathMenuCodeMap.js` 复用本文件的 `getMenuRBACCodes`，保证「菜单不显示」与「直链不可进」规则一致。
 *
 * 配置约定：
 * - `menuCode`：字符串，单码，用户需包含该码。
 * - `menuCodes`：字符串数组，至少命中其一。
 * - 二者皆无（或 `menuCodes` 为空数组）：不按菜单 RBAC 拦截，全员可见。
 * - `roles`：迁移期旧字段，语义同 `menuCodes`（非空数组时参与校验）。
 */

/**
 * 从单个菜单项配置解析「访问/展示」所需的菜单码列表。
 *
 * @param {object|undefined|null} menu - `menus.js` 中的单项（可含 children，本函数不递归子级）
 * @returns {string[]|null} 需要与用户 `menuCodes` 求交的码列表；`null` 表示不配置 RBAC、不按码拦截
 */
export function getMenuRBACCodes (menu) {
  if (menu == null) return null
  // 单码优先（与多码字段互斥使用即可）
  if (menu.menuCode != null && String(menu.menuCode).trim() !== '') {
    return [String(menu.menuCode)]
  }
  if (Array.isArray(menu.menuCodes) && menu.menuCodes.length > 0) {
    return menu.menuCodes
  }
  if (Array.isArray(menu.roles) && menu.roles.length > 0) {
    return menu.roles
  }
  return null
}

/**
 * 判断某菜单项是否应对当前用户展示（用于过滤侧栏 / 下拉菜单）。
 *
 * @param {object} menu
 * @param {string[]} userMenuCodes - 后端下发的 type=1 菜单码列表
 * @param {boolean} permissionCodesLoaded - 必须已拉取完成再判断，否则一律不展示需码项，避免闪烁误判
 * @returns {boolean}
 */
export function isMenuItemVisible (menu, userMenuCodes, permissionCodesLoaded) {
  const required = getMenuRBACCodes(menu)
  // 未配置码：始终可见
  if (!required || required.length === 0) {
    return true
  }
  // 码未就绪：不展示需权限的项（与路由守卫里 Spin 后再判断同理）
  if (!permissionCodesLoaded) {
    return false
  }
  const codes = Array.isArray(userMenuCodes) ? userMenuCodes : []
  return required.some(code => codes.includes(code))
}

/**
 * 递归剥离 RBAC 专用字段，得到可安全传给 Ant Design `Menu` 的 `items` 节点。
 * 避免 `menuCode` / `menuCodes` / `roles` 落到 DOM 或触发 React 未知 prop 警告。
 *
 * @param {object|null|undefined} menu
 * @returns {object|null}
 */
export function stripMenuRBACFields (menu) {
  if (!menu) return null
  const { menuCodes, menuCode, roles, children, sider, ...rest } = menu
  const next = { ...rest }
  if (children && Array.isArray(children)) {
    const mapped = children.map(stripMenuRBACFields).filter(Boolean)
    next.children = mapped.length > 0 ? mapped : undefined
  }
  return next
}
